import { BasePlugin } from 'Core/PluginCore/BasePlugin'
import { PluginConnector } from 'Core/PluginCore/PluginConnector'
import { PluginConfigType } from 'Core/PluginCore/PluginsManager'
import { Studio } from 'Core/Studio'
import { createReactElement, UpdatableReactElement } from 'shared/utils/react.utils'
import { CustomValidation, ValidationResult } from './component/CustomValidation'
import { CustomValidationIcon } from 'Components/common/Icons/CustomValidation'
import { PythonExecutor, PythonReturnMode } from 'shared/core/PythonExecutor/PythonExecutor'
import { GenerationTask } from 'Tasks/GenerationTask/GenerationTask'
import { ViolationSource } from 'shared/violations/types'
import { AttributeGroupEvent } from 'Core/AttributeCore/AttributeGroup/BaseAttributeGroup'

type CustomValidationPluginConfigType = {
    timeout: number
    validationScript: string
    packages: string[]
}

export class CustomValidationPlugin extends BasePlugin<
    PluginConfigType<CustomValidationPluginConfigType>
> {
    public readonly id = 'Custom Validation'
    private uiElement?: UpdatableReactElement
    private pythonExecutor: PythonExecutor = PythonExecutor.getInstance()
    private validationResult: ValidationResult | null = null

    constructor(
        config: PluginConfigType<CustomValidationPluginConfigType>,
        pluginConnectors: PluginConnector[]
    ) {
        super(config, pluginConnectors)
        this.pythonExecutor.installPackages(this.config.config.packages)
    }

    public initialize(studio: Studio) {
        this.studio = studio

        this.initializePythonExecutor()

        studio.workbench?.enqueueUiUpdate(accessor => {
            this.uiElement = createReactElement(CustomValidation, {
                runValidationCheck: this.runValidationCheck,
                isInitialized: () => this.pythonExecutor.initialized,
                getValidationResult: () => this.validationResult,
            })
            accessor.rightSidebar.addItem({
                element: this.uiElement.element,
                title: 'Custom Validation',
                icon: CustomValidationIcon,
                id: this.id,
            })

            this.attachAttributeChangeListener()
        })

        const data = this.getSelectedConnector().getData()
        if (data['text']) {
            data['text'].subscribe(() => {
                this.uiElement?.updater.forceUpdate()
                if (this.validationResult) {
                    this.removeValidationFailureViolation()
                    this.removeValidationErrorWarning()
                    this.addMandatoryViolation()
                }
            })
        }

        this.addMandatoryViolation()
    }

    public reset() {
        this.validationResult = null
        this.uiElement?.updater.forceUpdate()
    }

    private removeAttributeChangeListener = () => {
        const attributeManager = (this.studio?.taskManager.getTask() as GenerationTask)
            .attributeManager
        const attributeGroups = attributeManager?.getAllAttributeGroups()
        attributeGroups?.forEach(attributeGroup => {
            attributeGroup.off(AttributeGroupEvent.Change, this.onAttributeChange)
        })
    }

    private attachAttributeChangeListener = () => {
        const attributeManager = (this.studio?.taskManager.getTask() as GenerationTask)
            .attributeManager
        const attributeGroups = attributeManager?.getAllAttributeGroups()
        attributeGroups?.forEach(attributeGroup => {
            attributeGroup.on(AttributeGroupEvent.Change, this.onAttributeChange)
        })
    }

    private onAttributeChange = () => {
        this.removeValidationFailureViolation()
        this.removeValidationErrorWarning()
        this.addMandatoryViolation()
    }

    private initializePythonExecutor = async () => {
        await this.pythonExecutor.initialize()
        this.uiElement?.updater.forceUpdate()
    }

    public addMandatoryViolation() {
        this.pluginConnectors.forEach(connector => {
            this.studio?.inputManager.addViolation({
                id: `mandatory_custom_validation_${connector.id}`,
                message: 'You need to run custom validation check before submitting',
                type: 'error',
                source: ViolationSource.Plugin,
            })
        })
    }

    public addValidationFailureViolation() {
        this.pluginConnectors.forEach(connector => {
            this.studio?.inputManager.addViolation({
                id: `validation_failure_custom_validation_${connector.id}`,
                message: 'Validation checks did not pass. Please review your response.',
                type: 'error',
                source: ViolationSource.Plugin,
            })
        })
    }

    public addValidationErrorWarning() {
        this.pluginConnectors.forEach(connector => {
            this.studio?.inputManager.addViolation({
                id: `validation_error_custom_validation_${connector.id}`,
                message: 'Validation could not be completed due to an error. Please try again.',
                type: 'warning',
                source: ViolationSource.Plugin,
            })
        })
    }

    public removeValidationErrorWarning() {
        this.pluginConnectors.forEach(connector => {
            this.studio?.inputManager.removeViolation(
                `validation_error_custom_validation_${connector.id}`,
                'warning'
            )
        })
    }

    public removeValidationFailureViolation() {
        this.pluginConnectors.forEach(connector => {
            this.studio?.inputManager.removeViolation(
                `validation_failure_custom_validation_${connector.id}`,
                'error'
            )
        })
    }

    public removeMandatoryViolation() {
        this.pluginConnectors.forEach(connector => {
            this.studio?.inputManager.removeViolation(
                `mandatory_custom_validation_${connector.id}`,
                'error'
            )
        })
    }

    private runValidationCheck = async () => {
        try {
            this.removeValidationErrorWarning()
            this.removeValidationFailureViolation()

            const script = this.config.config.validationScript
            const draftOutputContainer = (
                this.studio?.taskManager.getTask() as GenerationTask
            ).taskState.getDraftOutputContainer()

            const inputContainer = this.studio?.taskConfig.assignment.task.input_container
            const jobTags = this.studio?.taskConfig.jobTags
            const userTags = this.studio?.taskConfig.userTags
            const extraInfo = { job_tags: jobTags, user_tags: userTags }

            this.pythonExecutor.setGlobalVariable('draft_output_container', draftOutputContainer)
            this.pythonExecutor.setGlobalVariable('input_container', inputContainer)
            this.pythonExecutor.setGlobalVariable('extra_info', extraInfo)

            this.validationResult = null
            const executableScript = this.createExecutableScript(script)
            const response = await this.pythonExecutor.runPythonAsync<ValidationResult>(
                executableScript,
                { returnMode: PythonReturnMode.Result, timeout: this.config.config.timeout }
            )

            this.removeMandatoryViolation()

            if (response.error) {
                console.error(response.error)
                this.handleValidationError()
            }

            if (response.result) {
                this.validationResult = response.result
                if (this.validationResult.result === 'fail') {
                    this.addValidationFailureViolation()
                }
                if (this.validationResult.result === 'error') {
                    this.addValidationErrorWarning()
                }
            } else {
                this.handleValidationError()
            }
        } catch (error) {
            console.error('Error in runValidationCheck:', error)
            this.handleValidationError()
        } finally {
            this.uiElement?.updater.forceUpdate()
        }
    }

    private handleValidationError = () => {
        this.validationResult = {
            result: 'error',
            messages: [
                `Validation could not be completed due to an error. Please try again.`,
                'If the issue persists, please submit your response as it is.',
            ],
        }
        this.addValidationErrorWarning()
    }

    private createExecutableScript = (script: string) => {
        return (
            script +
            `
import json

try:
    result = validate_data(draft_output_container, input_container, extra_info)
    final_result = json.dumps(result)
except Exception as e:
    import traceback
    error_result = {
        "error": str(e),
        "result": "error",
        "messages": [f"Error calling validate_data: {str(e)}", f"Traceback: {traceback.format_exc()}", "If the issue persists, please submit your response as it is."]
    }
    final_result = json.dumps(error_result)
final_result
`
        )
    }

    public destroy() {
        this.removeAttributeChangeListener()
    }
}
